import {
  MaterialReactTable,
  type MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import type { SsoClientDto } from "Infrastructure/Api/Api";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { useMemo } from "react";
import { Box, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.SsoClients;

type Props = {
  data: Array<SsoClientDto>;
  isLoading: boolean;
  onRowClick: (client: SsoClientDto) => void;
  onRotateSecret: (client: SsoClientDto) => void;
  onDelete: (client: SsoClientDto) => void;
};

export const SsoClientListTable = ({
  data,
  isLoading,
  onRowClick,
  onRotateSecret,
  onDelete,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();

  const columns = useMemo<Array<MRT_ColumnDef<SsoClientDto>>>(
    () => [
      {
        accessorKey: "clientId",
        header: t(RESOURCES.Table.ClientId),
        size: 180,
      },
      {
        accessorKey: "displayName",
        header: t(RESOURCES.Table.DisplayName),
        size: 180,
      },
      {
        id: "type",
        header: t(RESOURCES.Table.Type),
        size: 130,
        accessorFn: (row) =>
          row.isConfidential
            ? t(RESOURCES.Table.Confidential)
            : t(RESOURCES.Table.Public),
        Cell: ({ row }) => (
          <Chip
            size="small"
            color={row.original.isConfidential ? "primary" : "default"}
            label={
              row.original.isConfidential
                ? t(RESOURCES.Table.Confidential)
                : t(RESOURCES.Table.Public)
            }
          />
        ),
      },
      {
        id: "redirectUris",
        header: t(RESOURCES.Table.RedirectUris),
        size: 260,
        accessorFn: (row) => row.redirectUris.join(", "),
      },
      {
        id: "allowedRoles",
        header: t(RESOURCES.Table.AllowedRoles),
        size: 180,
        accessorFn: (row) => row.allowedRoles.join(", "),
        Cell: ({ row }) => (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {row.original.allowedRoles.map((role) => (
              <Chip key={role} label={role} size="small" />
            ))}
          </Stack>
        ),
      },
      {
        id: "allowedScopes",
        header: t(RESOURCES.Table.AllowedScopes),
        size: 150,
        accessorFn: (row) => row.allowedScopes.join(", "),
        Cell: ({ row }) => (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {row.original.allowedScopes.map((scope) => (
              <Chip key={scope} label={scope} size="small" variant="outlined" />
            ))}
          </Stack>
        ),
      },
      {
        id: "actions",
        header: t(RESOURCES.Table.Actions),
        size: 110,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box
            display="flex"
            gap={0.5}
            onClick={(event) => event.stopPropagation()}
          >
            {row.original.isConfidential && (
              <Tooltip title={t(RESOURCES.Table.RotateSecret)}>
                <IconButton
                  size="small"
                  onClick={() => onRotateSecret(row.original)}
                  aria-label={t(RESOURCES.Table.RotateSecret)}
                >
                  <KeyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t(RESOURCES.Table.Delete)}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(row.original)}
                aria-label={t(RESOURCES.Table.Delete)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t, onRotateSecret, onDelete],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    localization,
    enablePagination: false,
    enableColumnFilters: false,
    initialState: { density: "compact" },
    state: { isLoading },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.original),
      sx: { cursor: "pointer" },
    }),
  });

  return <MaterialReactTable table={table} />;
};
